/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CustomTaskQuickListModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the maintenance module.
----------------------------------------------------------------*/


import { FileCode } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { QuickSearchModal } from '../../../components/ui/QuickSearchModal';
import { useI18n } from '../../../hooks/useI18n';
import { CustomTask } from '../api';

interface Props {
    isOpen: boolean;
    currentItemId?: string;
    tasks: CustomTask[];
    isLoadingInitial: boolean;
    isSearching: boolean;
    searchValue: string;
    onSearchChange: (v: string) => void;
    onClose: () => void;
}

export function CustomTaskQuickListModal({ isOpen, currentItemId, tasks, isLoadingInitial, isSearching, searchValue, onSearchChange, onClose }: Props) {
    const { t } = useI18n();
    const navigate = useNavigate();

    const handleSelect = (task: CustomTask) => {
        onClose();
        navigate(`/admin/maintenance/tasks/${task.id}`);
    };

    return (
        <QuickSearchModal
            isOpen={isOpen}
            onClose={onClose}
            title={t('customTasks.actions.quickList')}
            items={tasks}
            isLoadingInitial={isLoadingInitial}
            isSearching={isSearching}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            onItemSelect={handleSelect}
            currentItemId={currentItemId}
            renderItem={(task, isCurrent) => (
                <button
                    onClick={() => { if (!isCurrent) handleSelect(task); }}
                    disabled={isCurrent}
                    className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent ? 'bg-primary/20 border-2 border-primary cursor-default' : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'}`}
                >
                    <div className="flex items-center gap-3">
                        <FileCode className="h-4 w-4 text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-foreground">{task.name}</span>
                                {isCurrent && <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">{t('customTasks.current')}</span>}
                            </div>
                            <div className="text-sm text-muted-foreground truncate">{task.scriptPath}</div>
                        </div>
                    </div>
                </button>
            )}
            searchPlaceholder={t('customTasks.quickListSearch')}
            minCharsMessage={t('customTasks.quickListMinChars')}
            emptyMessage={t('customTasks.quickListPrompt')}
            noResultsMessage={t('customTasks.quickListNoResults')}
            headerIcon={<FileCode className="h-5 w-5" />}
            minSearchChars={2}
        />
    );
}
